/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR488: Image Capture Diagnostics Banner
 * 
 * Proactive inline diagnostics for the flyer pipeline panel.
 * Surfaces stuck jobs, orphan jobs, and capture-to-OCR handoff issues
 * without requiring the operator to open the debug modal.
 */

import React, { useState, useEffect, useCallback } from "react";
import { 
  AlertCircle, AlertTriangle, Bug, Clock, ExternalLink, 
  Loader2, RefreshCw, Zap 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

// ─── Standardized Error Codes (FR6) ─────────────────────────────────────────

export const CAPTURE_ERROR_CODES = {
  WATCHDOG_TIMEOUT: "WATCHDOG_TIMEOUT",
  MISSING_BATCH_LINKAGE: "MISSING_BATCH_LINKAGE",
  DETACHED_RUN: "DETACHED_RUN",
  FINALIZATION_INCOMPLETE: "FINALIZATION_INCOMPLETE",
  FINALIZATION_FAILED: "FINALIZATION_FAILED",
  STORAGE_UPLOAD_PARTIAL: "STORAGE_UPLOAD_PARTIAL",
  STORAGE_UPLOAD_FAILED: "STORAGE_UPLOAD_FAILED",
  RECOVERY_FAILED: "RECOVERY_FAILED",
  CLEANUP_DRY_RUN: "CLEANUP_DRY_RUN",
  CLEANUP_EXECUTED: "CLEANUP_EXECUTED",
} as const;

export type CaptureErrorCode = keyof typeof CAPTURE_ERROR_CODES;

// ─── Error code descriptions for operator guidance ──────────────────────────

const ERROR_CODE_GUIDANCE: Record<string, string> = {
  WATCHDOG_TIMEOUT: "Job exceeded the expected execution time. Use 'Timeout Stale' in the Debug modal to mark it as timed out, then retry.",
  MISSING_BATCH_LINKAGE: "Capture job is not linked to a batch run. Use the Debug modal to inspect and recover.",
  DETACHED_RUN: "This capture run is detached from its merchant source. Check if the merchant source was deleted.",
  FINALIZATION_INCOMPLETE: "Capture completed but finalization did not write all expected artifacts. Check manifest completeness.",
  FINALIZATION_FAILED: "Finalization step failed. Check error logs and retry the capture.",
  STORAGE_UPLOAD_PARTIAL: "Some pages failed to upload to storage. Re-run capture to fill gaps.",
  STORAGE_UPLOAD_FAILED: "Storage upload failed completely. Check storage bucket permissions and retry.",
};

// ─── Diagnostics state ──────────────────────────────────────────────────────

interface CaptureDiagnostics {
  stuckJobCount: number;
  stuckJobs: Array<{
    id: string;
    adapter_key: string | null;
    merchant_id: string | null;
    status: string;
    stage: string | null;
    started_at: string | null;
    heartbeat_at: string | null;
    error_code: string | null;
    error_message: string | null;
    age_seconds: number | null;
  }>;
  orphanCount: number;
  detachedCount: number;
  failedJobCount: number;
  timedOutJobCount: number;
}

interface Props {
  batchRunId: string;
  /** Callback to open the full debug modal */
  onOpenDebugModal?: () => void;
  /** If true, poll for updates */
  autoRefresh?: boolean;
}

export function ImageCaptureDiagnosticsBanner({
  batchRunId,
  onOpenDebugModal,
  autoRefresh = true,
}: Props) {
  const [diag, setDiag] = useState<CaptureDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDiagnostics = useCallback(async () => {
    try {
      const [stuckRes, orphanRes, detachedRes, statusRes] = await Promise.all([
        // Stuck jobs for this batch
        (supabase as any)
          .from("v_flyer_image_capture_stuck_jobs")
          .select("id, adapter_key, merchant_id, status, stage, started_at, heartbeat_at, error_code, error_message, age_ms")
          .eq("batch_run_id", batchRunId)
          .order("age_ms", { ascending: false })
          .limit(5),
        // Global orphan count
        (supabase as any)
          .from("v_flyer_image_capture_orphan_jobs")
          .select("id", { count: "exact", head: true }),
        // Global detached count
        (supabase as any)
          .from("v_flyer_image_capture_detached_legacy_jobs")
          .select("id", { count: "exact", head: true }),
        // Failed/timed out for this batch
        supabase
          .from("flyer_image_capture_test_runs")
          .select("status")
          .eq("batch_run_id", batchRunId)
          .in("status", ["FAILED", "TIMED_OUT"]),
      ]);

      const stuckJobs = (stuckRes.data ?? []).map((j: any) => ({
        ...j,
        age_seconds: j.age_ms ? Math.floor(j.age_ms / 1000) : null,
      }));

      const failedRows = statusRes.data ?? [];
      const failedCount = failedRows.filter((r: any) => r.status === "FAILED").length;
      const timedOutCount = failedRows.filter((r: any) => r.status === "TIMED_OUT").length;

      setDiag({
        stuckJobCount: stuckJobs.length,
        stuckJobs,
        orphanCount: orphanRes.count ?? 0,
        detachedCount: detachedRes.count ?? 0,
        failedJobCount: failedCount,
        timedOutJobCount: timedOutCount,
      });
    } catch (err) {
      console.error("[CaptureDiagnostics] fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [batchRunId]);

  useEffect(() => {
    fetchDiagnostics();
  }, [fetchDiagnostics]);

  // Auto-refresh every 15s when there are issues
  useEffect(() => {
    if (!autoRefresh || !diag) return;
    const hasIssues = diag.stuckJobCount > 0 || diag.failedJobCount > 0 || diag.timedOutJobCount > 0;
    if (!hasIssues) return;
    const interval = setInterval(fetchDiagnostics, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, diag, fetchDiagnostics]);

  // Nothing to show if no issues
  if (loading) return null;
  if (!diag) return null;

  const hasIssues =
    diag.stuckJobCount > 0 ||
    diag.failedJobCount > 0 ||
    diag.timedOutJobCount > 0 ||
    diag.orphanCount > 0;

  if (!hasIssues) return null;

  return (
    <div className="space-y-2">
      {/* Stuck jobs alert */}
      {diag.stuckJobCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive mt-px flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <div>
              <strong className="text-destructive">
                {diag.stuckJobCount} stuck capture job{diag.stuckJobCount !== 1 ? "s" : ""} detected
              </strong>
              <span className="text-muted-foreground ml-1">
                — RUNNING with no heartbeat. Pipeline progress may be blocked.
              </span>
            </div>
            {/* Show top stuck jobs */}
            {diag.stuckJobs.slice(0, 3).map((j) => (
              <div key={j.id} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span className="font-mono">{j.id.slice(0, 8)}</span>
                {j.adapter_key && <Badge variant="outline" className="text-[9px] h-4 px-1">{j.adapter_key.split("__")[0]}</Badge>}
                {j.stage && <span className="text-warning">@{j.stage}</span>}
                {j.age_seconds != null && (
                  <span>age: {j.age_seconds > 60 ? `${Math.floor(j.age_seconds / 60)}m` : `${j.age_seconds}s`}</span>
                )}
                {j.error_code && (
                  <Badge variant="destructive" className="text-[9px] h-4 px-1">{j.error_code}</Badge>
                )}
              </div>
            ))}
            <div className="text-muted-foreground">
              <strong>Next action:</strong> Open Debug modal → <em>Timeout Stale</em>, then retry capture.
            </div>
          </div>
          {onOpenDebugModal && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] flex-shrink-0" onClick={onOpenDebugModal}>
              <Bug className="h-3 w-3 mr-1" /> Debug
            </Button>
          )}
        </div>
      )}

      {/* Failed / timed out jobs */}
      {(diag.failedJobCount > 0 || diag.timedOutJobCount > 0) && diag.stuckJobCount === 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-xs">
          <AlertCircle className="h-3.5 w-3.5 text-warning mt-px flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span>
              {diag.failedJobCount > 0 && (
                <strong className="text-warning">{diag.failedJobCount} failed</strong>
              )}
              {diag.failedJobCount > 0 && diag.timedOutJobCount > 0 && " + "}
              {diag.timedOutJobCount > 0 && (
                <strong className="text-warning">{diag.timedOutJobCount} timed out</strong>
              )}
              {" "}capture job{(diag.failedJobCount + diag.timedOutJobCount) !== 1 ? "s" : ""}.
              {" "}Check error details in Debug modal, then requeue or retry.
            </span>
          </div>
          {onOpenDebugModal && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] flex-shrink-0" onClick={onOpenDebugModal}>
              <Bug className="h-3 w-3 mr-1" /> Debug
            </Button>
          )}
        </div>
      )}

      {/* Orphan jobs (global) */}
      {diag.orphanCount > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            <strong>{diag.orphanCount}</strong> orphan job{diag.orphanCount !== 1 ? "s" : ""} reference deleted merchant sources (global).
          </span>
        </div>
      )}
    </div>
  );
}
